import React, { useCallback, useMemo, useState } from 'react'
import { useTranslate } from 'src/localization/Trns'
import {
  StreamInterruptBatch,
  StreamInterruptDecision,
} from 'src/queries/agent/network/agent.models'

import styles from './InterruptBatchRenderer.style'
import InterruptCard from './InterruptCard'

interface InterruptBatchRendererProps {
  batch: StreamInterruptBatch
  onSubmit: (decisions: StreamInterruptDecision[]) => void
  disabled?: boolean
}

/**
 * Renders a batch of interrupts with individual action selection
 * and a submit button to resolve all at once
 */
const InterruptBatchRenderer: React.FC<InterruptBatchRendererProps> = ({
  batch,
  onSubmit,
  disabled = false,
}) => {
  const translate = useTranslate()

  // Initialize decisions as null - require explicit user action for each interrupt
  const [decisions, setDecisions] = useState<
    (StreamInterruptDecision | null)[]
  >(() => batch.interrupts.map(() => null))

  const handleDecisionChange = useCallback(
    (index: number, decision: StreamInterruptDecision): void => {
      setDecisions((prev) => {
        const newDecisions = [...prev]
        newDecisions[index] = decision
        return newDecisions
      })
    },
    [],
  )

  // Check if all decisions have been made
  const allDecisionsMade = useMemo(
    () => decisions.every((d) => d !== null),
    [decisions],
  )

  const handleSubmit = useCallback((): void => {
    // Only submit if all decisions are made (filter removes nulls for type safety)
    const validDecisions = decisions.filter(
      (d): d is StreamInterruptDecision => d !== null,
    )
    if (validDecisions.length === batch.interrupts.length) {
      onSubmit(validDecisions)
    }
  }, [decisions, batch.interrupts.length, onSubmit])

  // Get summary of decisions
  const decisionSummary = useMemo(() => {
    const counts = { approve: 0, reject: 0, pending: 0 }
    decisions.forEach((d) => {
      if (d === null) {
        counts.pending++
      } else {
        counts[d.type]++
      }
    })
    const parts: string[] = []
    if (counts.approve > 0) parts.push(`${counts.approve} approved`)
    if (counts.reject > 0) parts.push(`${counts.reject} rejected`)
    if (counts.pending > 0) parts.push(`${counts.pending} pending`)
    return parts.join(', ')
  }, [decisions])

  return (
    <div css={styles} className="InterruptBatchRenderer">
      {batch.interrupts.length > 1 && (
        <div className="batch-header">
          <span className="batch-title">
            {translate('Actions Requiring Approval')}
          </span>
          <span className="batch-count">
            {batch.interrupts.length} {translate('items')}
          </span>
        </div>
      )}

      <div className="interrupts-list">
        {batch.interrupts.map((interrupt, index) => (
          <InterruptCard
            key={interrupt.index}
            interrupt={interrupt}
            decision={decisions[index]}
            onDecisionChange={(decision): void =>
              handleDecisionChange(index, decision)
            }
            disabled={disabled}
          />
        ))}
      </div>

      <div className="submit-wrapper">
        <div className="submit-info">{decisionSummary}</div>
        <button
          className="submit-button"
          onClick={handleSubmit}
          disabled={disabled || !allDecisionsMade}
          aria-label={translate('Confirm all interrupt decisions')}
        >
          {translate('Confirm All')} {'('}
          {batch.interrupts.length}
          {')'}
        </button>
      </div>
    </div>
  )
}

export default InterruptBatchRenderer
